import { useCallback, useEffect, useState } from "react";
import type {
  CaptureFormat,
  CaptureKind,
  DeviceState,
  ResolvedDevice,
} from "@cuekards/core/media-devices";
import {
  CAPTURE_KINDS,
  CAPTURE_KIND_NOUN,
  FRAME_RATE_PRESETS,
  RESOLUTION_PRESETS,
  displayLabel,
  isCaptureKind,
  selectableDevices,
} from "@cuekards/core/media-devices";

/**
 * The devices section of the control window (ticket 11, FR-41/FR-42).
 *
 * This is the one place that can see the hardware: `navigator.mediaDevices`
 * exists only where a document does. It enumerates, reports the result to the
 * host — which owns the state and the persisted choice — and re-enumerates on
 * every `devicechange`, so plugging a camera in or pulling it out is reflected
 * without a restart. Everything it draws comes back from the host as a
 * {@link DeviceState}, including the notice raised when a remembered device is
 * no longer there.
 */
export function DevicePicker({
  disabled = false,
}: {
  disabled?: boolean;
}): JSX.Element {
  const [state, setState] = useState<DeviceState | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [outputDirectory, setOutputDirectory] = useState("");

  // Enumerate now and on every hot-plug, reporting each result to main (FR-41).
  useEffect(() => {
    const media = navigator.mediaDevices;
    let cancelled = false;
    let unlocked = false;

    const enumerate = async (): Promise<void> => {
      let devices = await media.enumerateDevices();
      // Until media permission has been granted Chromium returns placeholders —
      // blank ids, blank labels — and FR-41 wants names a human recognises. One
      // probe stream unlocks them. It is attempted at most once per session:
      // repeating it on every hot-plug would re-open the microphone, which is
      // exactly what FR-31's one-open invariant exists to prevent.
      if (!unlocked && devices.some((d) => isCaptureKind(d.kind) && !d.label)) {
        unlocked = true;
        const granted = await unlockLabels();
        if (cancelled) return;
        setBlocked(!granted);
        devices = await media.enumerateDevices();
      }
      if (!cancelled) window.cuekards.reportDevices(devices.map(toRaw));
    };

    const onDeviceChange = (): void => void enumerate();
    void enumerate();
    media.addEventListener("devicechange", onDeviceChange);
    return () => {
      cancelled = true;
      media.removeEventListener("devicechange", onDeviceChange);
    };
  }, []);

  // Follow main's device state — the selection, what it resolves to, and any
  // notice about a device that has gone.
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getDeviceState().then((s) => {
      if (!cancelled) setState(s);
    });
    const off = window.cuekards.onDeviceStateChanged(setState);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // Where finished takes land (FR-48/FR-62). Fixed in v1, so it is read once and
  // never changes underneath us.
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getOutputDirectory().then((path) => {
      if (!cancelled) setOutputDirectory(path);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setFormat = useCallback(
    (patch: CaptureFormat) => {
      // The host sanitises and treats "nothing usable" as native, so a cleared
      // dropdown needs no special case here.
      window.cuekards.setCaptureFormat({ ...state?.selection.format, ...patch });
    },
    [state],
  );

  if (!state) return <section className="devices" aria-busy="true" />;

  const format = state.selection.format;
  const resolution =
    format?.width && format.height ? `${format.width}x${format.height}` : "";

  // Nothing overridden means nothing to reset (FR-64).
  const overridden =
    state.selection.choices !== undefined || state.selection.format !== undefined;

  return (
    <section className="devices settings-screen">
      {CAPTURE_KINDS.map((kind) => (
        <DeviceSelect
          key={kind}
          kind={kind}
          state={state}
          resolved={state.resolved[kind]}
          disabled={disabled}
        />
      ))}

      <label className="device-row">
        <span>Resolution</span>
        <select
          value={resolution}
          disabled={disabled}
          onChange={(e) => {
            const [w, h] = e.target.value.split("x");
            setFormat({
              width: w ? Number(w) : undefined,
              height: h ? Number(h) : undefined,
            });
          }}
        >
          <option value="">Native (device default)</option>
          {RESOLUTION_PRESETS.map((preset) => (
            <option
              key={preset.label}
              value={`${preset.width}x${preset.height}`}
            >
              {preset.label}
            </option>
          ))}
        </select>
      </label>

      <label className="device-row">
        <span>Framerate</span>
        <select
          value={format?.frameRate ?? ""}
          disabled={disabled}
          onChange={(e) =>
            setFormat({
              frameRate: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        >
          <option value="">Native (device default)</option>
          {FRAME_RATE_PRESETS.map((fps) => (
            <option key={fps} value={fps}>
              {fps} fps
            </option>
          ))}
        </select>
      </label>

      {!state.enumerated && (
        <p className="device-status">Looking for devices…</p>
      )}

      {/* A blocked permission is why the list is empty; without saying so, the
        * user sees "No camera detected" and has nowhere to go.
        *
        * The only host-specific copy left in this package, and knowingly so: it
        * names a Windows settings path, where a tab's answer is not a settings
        * path at all but the browser's own permission prompt. Left as the
        * desktop's wording rather than generalised now, because the web's version
        * of this is a different screen — the capability gate and the permission it
        * asks for are web ticket 17 — and inventing a copy seam before there is a
        * second string to put in it would be guessing at its shape. Whoever writes
        * that ticket should split this. `host-agnostic.test.ts` cannot catch it:
        * it reads imports, and this is prose. */}
      {blocked && (
        <p className="device-notice" role="status">
          Windows is blocking access to the camera and microphone, so no devices
          can be listed. Allow desktop apps to use them in Settings → Privacy &amp;
          security → Camera and Microphone.
        </p>
      )}

      {state.notices.map((notice) => (
        <p className="device-notice" role="status" key={notice}>
          {notice}
        </p>
      ))}

      {/* The output folder (FR-62). Read-only, because FR-48 fixes it in v1 —
        * shown at all so there is one place in the app that answers "where did
        * that take go", with a way to get there. */}
      <label className="settings-row">
        <span>Recordings</span>
        <input
          type="text"
          readOnly
          value={outputDirectory}
          aria-label="Output folder"
          title="Fixed in this version"
        />
        <button
          type="button"
          onClick={() => window.cuekards.revealOutputDirectory()}
          disabled={outputDirectory === ""}
          title="Open this folder in Explorer"
        >
          Reveal
        </button>
      </label>

      <div className="settings-actions">
        {/* FR-64. Back to the system's own camera and microphone at whatever
          * resolution they run natively — which is what cuekards does before you
          * choose anything. */}
        <button
          type="button"
          className="settings-reset"
          onClick={() => window.cuekards.resetDevices()}
          disabled={disabled || !overridden}
          title="Use the system devices at their native format"
        >
          Reset to defaults
        </button>
      </div>
    </section>
  );
}

/** One device dropdown: the system default, every device of the kind, and — when
 * the remembered choice has been unplugged — that choice too, so the select
 * still shows what the user asked for beside the notice explaining the
 * stand-in. */
function DeviceSelect({
  kind,
  state,
  resolved,
  disabled,
}: {
  kind: CaptureKind;
  state: DeviceState;
  resolved: ResolvedDevice;
  disabled: boolean;
}): JSX.Element {
  const noun = CAPTURE_KIND_NOUN[kind];
  const title = `${noun[0]!.toUpperCase()}${noun.slice(1)}`;
  const missing = resolved.fallback ? resolved.requested : undefined;

  return (
    <label className="device-row">
      <span>{title}</span>
      <select
        value={resolved.requested?.deviceId ?? ""}
        disabled={disabled}
        aria-label={title}
        onChange={(e) =>
          window.cuekards.selectDevice(kind, e.target.value || null)
        }
      >
        <option value="">
          {resolved.systemDefault
            ? `System default (${displayLabel(resolved.systemDefault)})`
            : `System default (no ${noun} found)`}
        </option>
        {selectableDevices(state.devices, kind).map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label}
          </option>
        ))}
        {missing && (
          <option value={missing.deviceId}>{missing.label} (unplugged)</option>
        )}
      </select>
    </label>
  );
}

function toRaw(device: MediaDeviceInfo): {
  deviceId: string;
  kind: string;
  label: string;
} {
  return { deviceId: device.deviceId, kind: device.kind, label: device.label };
}

/** Grant the page media permission so `enumerateDevices` returns real labels,
 * then release the hardware immediately. Returns whether permission was
 * granted: a refusal is not an error, but it is worth saying out loud, since
 * it leaves the device list empty. */
async function unlockLabels(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    for (const track of stream.getTracks()) track.stop();
    return true;
  } catch {
    return false;
  }
}
